import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { SingleValue } from "react-select";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./buyCreditsTab.module.css";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import { isAuthSelector } from "../../../../services/selectors/auth.selectors";
import { useGetAllCurrenciesQuery } from "../../../../services/api/utils.api";
import {
  CreditType,
  FeatureCreditPackageDto,
  useListFeatureCreditPackagesQuery,
  useBuyFeatureCreditsMutation,
} from "../../../../services/api/featureCreditPackagesApi";
import {
  useLazyGetUsersByCompanyIdWithRecommendsQuery,
  useGetUserEntitlementsQuery,
} from "../../../../services/api/user.api";
import { amountUnitLabel, creditTypeLabel, featureOptionLabel, mapBuyError } from "../../../../utils/featureCreditPackages";
import { isStaffRole, STAFF_ROLE_IDS } from "../../../../utils/featureCreditsAuth";
import { TLabeledItem } from "../../../../types/types";
import ErrorPopup from "../../errorPopup/errorPopup";
import Modal from "../../modal/Modal";
import { packageFeatureLabel } from "../../../../utils/utils";

const CREDIT_TYPES: CreditType[] = ["token", "count", "seat"];

type PaymentBannerVariant = "processing" | "success" | "failure";

interface PaymentBannerProps {
  variant: PaymentBannerVariant;
  onDismiss: () => void;
}

function PaymentBanner({ variant, onDismiss }: PaymentBannerProps) {
  const message =
    variant === "success"
      ? t("PAYMENT_SUCCESS_BANNER")
      : variant === "failure"
        ? t("PAYMENT_FAILURE_BANNER")
        : t("PAYMENT_PROCESSING_CHECK_BACK");

  const variantClass =
    variant === "success"
      ? styles.statusBannerSuccess
      : variant === "failure"
        ? styles.statusBannerError
        : "";

  return (
    <div className={`${styles.statusBanner} ${variantClass}`}>
      <span>{message}</span>
      <button
        type="button"
        className={styles.statusBannerDismiss}
        aria-label={t("DISMISS")}
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}



type ScopeType = "self" | "company" | "specific";

interface ScopeSelectorProps {
  packageCreditType: CreditType;
  selectedScope: ScopeType | null;
  selectedUserId: number | null;
  userError: boolean;
  staffMembers: TLabeledItem[];
  onScopeChange: (scope: ScopeType) => void;
  onUserChange: (userId: number | null) => void;
}

function ScopeSelector({
  packageCreditType,
  selectedScope,
  selectedUserId,
  userError,
  staffMembers,
  onScopeChange,
  onUserChange,
}: ScopeSelectorProps) {
  const isSeat = packageCreditType === "seat";

  if (isSeat) {
    return (
      <div className={styles.scopeSection}>
        <label className={styles.scopeLabel}>{t("SCOPE_TYPE")}</label>
        <div className={styles.scopeDisabled}>
          {t("SCOPE_WHOLE_COMPANY")}
        </div>
      </div>
    );
  }

  const scopeOptions: TLabeledItem[] = [
    { value: "self", label: t("SCOPE_MYSELF") },
    { value: "company", label: t("SCOPE_WHOLE_COMPANY") },
    { value: "specific", label: t("SCOPE_SPECIFIC_STAFF_MEMBER") },
  ];

  const selectedScopeOption = selectedScope
    ? scopeOptions.find((o) => o.value === selectedScope) ?? null
    : null;

  const selectedUserOption = selectedUserId
    ? staffMembers.find((o) => o.value === selectedUserId.toString()) ?? null
    : null;

  return (
    <div className={styles.scopeSection}>
      <label htmlFor="scope" className={styles.scopeLabel}>
        {t("SCOPE_TYPE")}
      </label>
      <Select
        inputId="scope"
        options={scopeOptions}
        value={selectedScopeOption}
        onChange={(newValue: SingleValue<TLabeledItem>) => {
          const scope = newValue?.value as ScopeType | undefined;
          if (scope) {
            onScopeChange(scope);
            if (scope !== "specific") onUserChange(null);
          }
        }}
        placeholder={t("SELECT_SCOPE")}
        className={styles.scopeSelect}
        menuPosition="fixed"
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />

      {selectedScope === "specific" && (
        <div className={styles.userSection}>
          <label htmlFor="targetUser" className={styles.scopeLabel}>
            {t("SELECT_STAFF_MEMBER")}
          </label>
          <Select
            inputId="targetUser"
            options={staffMembers}
            value={selectedUserOption}
            onChange={(newValue: SingleValue<TLabeledItem>) => {
              onUserChange(newValue ? Number(newValue.value) : null);
            }}
            placeholder={t("SELECT_STAFF_MEMBER")}
            className={styles.scopeSelect}
            isClearable
            menuPosition="fixed"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          {userError && (
            <span className={styles.userError}>{t("SELECT_USER_ERROR")}</span>
          )}
        </div>
      )}
    </div>
  );
}

interface PackageCardProps {
  pkg: FeatureCreditPackageDto;
  isSelected: boolean;
  selectedCurrencyId: number | null;
  currencyLabel: (currencyId: number) => string;
  onSelectPackage: (pkg: FeatureCreditPackageDto) => void;
  onSelectCurrency: (currencyId: number) => void;
}

function PackageCard({
  pkg,
  isSelected,
  selectedCurrencyId,
  currencyLabel,
  onSelectPackage,
  onSelectCurrency,
}: PackageCardProps) {
  const currencyOptions: TLabeledItem[] = pkg.prices.map((price) => ({
    value: price.currencyId.toString(),
    label: `${currencyLabel(price.currencyId)} ${price.priceAfterTax}`,
  }));
  const selectedCurrencyOption =
    selectedCurrencyId != null
      ? currencyOptions.find((o) => o.value === selectedCurrencyId.toString()) ?? null
      : null;

  return (
    <div className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}>
      <button type="button" className={styles.cardMain} onClick={() => onSelectPackage(pkg)}>
        <span className={styles.cardFeature}>{packageFeatureLabel(pkg)}</span>
        <span className={styles.cardName}>
          {pkg.name}
          <span className={styles.cardNameEn}>{pkg.nameEn}</span>
        </span>
        <span className={styles.cardAmount}>
          {pkg.amount.toLocaleString()} {amountUnitLabel(pkg.creditType)}
        </span>
        {pkg.description && <span className={styles.cardDescription}>{pkg.description}</span>}
      </button>

      <div className={styles.cardPriceRow}>
        {pkg.prices.length === 1 ? (
          <span className={styles.cardPrice}>
            {currencyLabel(pkg.prices[0].currencyId)} {pkg.prices[0].priceAfterTax}
          </span>
        ) : (
          <Select
            inputId={`currency-${pkg.id}`}
            options={currencyOptions}
            value={selectedCurrencyOption}
            placeholder={t("SELECT_CURRENCY")}
            className={styles.currencySelect}
            menuPosition="fixed"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            onChange={(newValue: SingleValue<TLabeledItem>) => {
              onSelectPackage(pkg);
              if (newValue) onSelectCurrency(Number(newValue.value));
            }}
          />
        )}
      </div>
    </div>
  );
}

export const BuyCreditsTab: React.FC = () => {
  const admin = useAppSelector(adminSelector);
  const isAuth = useAppSelector(isAuthSelector);
  const { data: currencies = [] } = useGetAllCurrenciesQuery();
  const { data: packages = [], isLoading, isError } = useListFeatureCreditPackagesQuery(false);
  const [getStaffMembers, { data: staffResponse }] = useLazyGetUsersByCompanyIdWithRecommendsQuery();
  const [buyFeatureCredits, { isLoading: isBuyLoading }] = useBuyFeatureCreditsMutation();
  const { refetch: refetchEntitlements } = useGetUserEntitlementsQuery(undefined, {
    skip: !isAuth || !admin.id,
  });

  const [selectedPackage, setSelectedPackage] = useState<FeatureCreditPackageDto | null>(null);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | null>(null);
  const [selectedScope, setSelectedScope] = useState<ScopeType | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userError, setUserError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [paymentBanner, setPaymentBanner] = useState<PaymentBannerVariant | null>(null);
  const paymentWindowRef = useRef<Window | null>(null);

  const dismissPaymentBanner = () => setPaymentBanner(null);

  // While the CardCom popup is open, listen for the result reported by the
  // payment-callback page (public/payment-callback.html) — the page the backend
  // redirects CardCom to after payment. It closes itself and reports back here,
  // so the success/failure page never appears in the admin tab.
  //
  // CardCom may send Cross-Origin-Opener-Policy, which severs window.opener and
  // breaks both postMessage and opener-side close(). So we listen on a same-
  // origin BroadcastChannel (survives COOP) as the primary signal, with the
  // window "message" postMessage as a fallback for older browsers.
  useEffect(() => {
    if (paymentBanner !== "processing") return;

    const finish = (status: unknown) => {
      paymentWindowRef.current?.close();
      paymentWindowRef.current = null;
      if (status === "success") {
        refetchEntitlements();
        setPaymentBanner("success");
      } else {
        setPaymentBanner("failure");
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.source !== "bprev-payment-callback") return;
      finish(event.data.status);
    };
    window.addEventListener("message", handleMessage);

    const channel =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel("bprev-payment-callback")
        : null;
    if (channel) {
      channel.onmessage = (event) => finish(event.data?.status);
    }

    const poll = window.setInterval(() => {
      if (paymentWindowRef.current?.closed) {
        paymentWindowRef.current = null;
        window.clearInterval(poll);
        // Manual close: we can't know the outcome, so leave the neutral
        // "check back shortly" banner rather than claiming success/failure.
      }
    }, 800);

    return () => {
      window.removeEventListener("message", handleMessage);
      channel?.close();
      window.clearInterval(poll);
    };
  }, [paymentBanner, refetchEntitlements]);

  const staffMembers = useMemo(
    () =>
      (staffResponse?.Users ?? [])
        .filter((u) => isStaffRole(u.Users.RoleId) && Number(u.Users.Id) !== Number(admin.id))
        .map((u) => {
          const name = u.Users.FullName || `${u.Users.FirstName} ${u.Users.LastName}`;
          return { value: u.Users.Id.toString(), label: name };
        }),
    [staffResponse, admin.id]
  );

  const currencyLabel = (currencyId: number): string => {
    const currency = currencies.find((c) => c.id === currencyId);
    if (!currency) return `#${currencyId}`;
    return i18n.language === "he" ? currency.currencyName : currency.currencyCode;
  };

  const selectPackage = (pkg: FeatureCreditPackageDto) => {
    if (selectedPackage?.id === pkg.id) return;
    setSelectedPackage(pkg);
    setSelectedCurrencyId(pkg.prices.length === 1 ? pkg.prices[0].currencyId : null);
    setSelectedScope(pkg.creditType === "seat" ? "company" : null);
    setSelectedUserId(null);
    setUserError(false);
  };

  const handleScopeChange = (scope: ScopeType) => {
    setSelectedScope(scope);
    setUserError(false);
    if (scope === "specific" && !staffResponse) {
      getStaffMembers({
        companyIds: [admin.companyId],
        roleIds: STAFF_ROLE_IDS,
        fakeCompanyIds: [-1],
      });
    }
  };

  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
    setUserError(false);
  };

  const handleBuy = async () => {
    if (!selectedPackage || !selectedCurrencyId || !selectedScope) return;

    if (selectedScope === "specific" && !selectedUserId) {
      setUserError(true);
      return;
    }

    const payload =
      selectedScope === "self"
        ? { scope: "self" as const }
        : selectedScope === "company"
          ? { scope: "company" as const }
          : { scope: "company" as const, targetUserId: selectedUserId };

    try {
      const result = await buyFeatureCredits({
        packageId: selectedPackage.id,
        currencyId: selectedCurrencyId,
        ...payload,
      }).unwrap();

      // Backend may return PascalCase or camelCase (see ICreatePaymentResponse).
      const url = result.Url ?? (result as { url?: string }).url;
      if (url) {
        // Open CardCom in a popup so the admin page stays put. After payment
        // the backend redirects the popup to payment-callback.html, which posts
        // the result back here (handled by the effect above) and closes itself.
        const popup = window.open(url, "bprevPayment", "width=500,height=720");
        if (!popup) {
          // Popup blocked — fall back to the original full-tab navigation.
          window.location.href = url;
          return;
        }
        paymentWindowRef.current = popup;
        setPaymentBanner("processing");
        setSelectedPackage(null);
        setSelectedCurrencyId(null);
        setSelectedScope(null);
        setSelectedUserId(null);
      } else {
        // A 200 with no payment URL (e.g. NoAmountDue) would otherwise be a
        // silent dead end — surface it instead of doing nothing.
        setErrorMessage(t("BUY_SESSION_FAILED_ERROR"));
      }
    } catch (error) {
      setErrorMessage(mapBuyError(error));
    }
  };

  const handleErrorClose = () => {
    setErrorMessage(null);
  };

  // The catalog endpoint may or may not already scope results to (global ∪
  // caller's company); filter defensively so a company-scoped pack that
  // isn't the admin's own company never shows up as purchasable.
  const groups = useMemo(() => {
    const purchasable = packages.filter(
      (pkg) =>
        pkg.prices.length > 0 && (pkg.companyId == null || pkg.companyId === admin.companyId)
    );
    return CREDIT_TYPES.map((creditType) => ({
      creditType,
      packages: purchasable.filter((pkg) => pkg.creditType === creditType),
    })).filter((group) => group.packages.length > 0);
  }, [packages, admin.companyId]);

  const isBuyDisabled =
    isBuyLoading ||
    !selectedPackage ||
    !selectedCurrencyId ||
    !selectedScope;

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>💳</span>
          <div className={styles.sectionTitleGroup}>
            <span className={styles.sectionTitle}>{t("BUY_CREDITS")}</span>
            <span className={styles.sectionSubtitle}>{t("BUY_CREDITS_DESC")}</span>
          </div>
        </div>
        <div className={styles.sectionBody}>
          {paymentBanner && (
            <PaymentBanner variant={paymentBanner} onDismiss={dismissPaymentBanner} />
          )}
          {isLoading ? (
            <div className={styles.loading}>{t("LOADING")}</div>
          ) : isError ? (
            <p className={styles.loading}>{t("SOMETHING_WENT_WRONG_TRY_AGAIN")}</p>
          ) : groups.length === 0 ? (
            <p className={styles.loading}>{t("NO_CREDIT_PACKAGES_AVAILABLE")}</p>
          ) : (
            <div className={styles.picker}>
              {groups.map((group) => (
                <div key={group.creditType} className={styles.group}>
                  <h4 className={styles.groupTitle}>{creditTypeLabel(group.creditType)}</h4>
                  <div className={styles.cardGrid}>
                    {group.packages.map((pkg) => (
                      <PackageCard
                        key={pkg.id}
                        pkg={pkg}
                        isSelected={selectedPackage?.id === pkg.id}
                        selectedCurrencyId={selectedPackage?.id === pkg.id ? selectedCurrencyId : null}
                        currencyLabel={currencyLabel}
                        onSelectPackage={selectPackage}
                        onSelectCurrency={setSelectedCurrencyId}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {selectedPackage && (
                <div className={styles.checkoutSection}>
                  <ScopeSelector
                    packageCreditType={selectedPackage.creditType}
                    selectedScope={selectedScope}
                    selectedUserId={selectedUserId}
                    userError={userError}
                    staffMembers={staffMembers}
                    onScopeChange={handleScopeChange}
                    onUserChange={handleUserChange}
                  />

                  <button
                    type="button"
                    className={`button ${styles.buyButton}`}
                    disabled={isBuyDisabled}
                    onClick={handleBuy}
                  >
                    {isBuyLoading ? t("PROCESSING") : t("BUY_CREDITS")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {errorMessage && (
        <Modal onClose={handleErrorClose}>
          <ErrorPopup text={errorMessage} onClose={handleErrorClose} />
        </Modal>
      )}
    </div>
  );
};
